/* ============================================================
   SCNT — Shop filter drawer (right side panel)
   - Opens from any [data-scnt-filter-open] button.
   - Best worn for / Scent direction: multi-select chips & rows.
   - Scent feel / Presence: draggable sliders (single level).
   - Active selections shown as removable pills.
   - Apply filters the rendered product grid client-side (show/hide)
     by reading each card's data-scnt-* attributes, so it works
     without any Search & Discovery setup. Selection is mirrored to
     the URL (filter.p.m.<key>=<value>) for shareable/persistent state.
   ============================================================ */
(function () {
  'use strict';

  /* selection key -> card data attribute + whether the card holds a list */
  var ATTR = {
    'custom.best_occasion': { attr: 'scntOccasion', list: true },
    'custom.scent_feel': { attr: 'scntFeel', list: false },
    'custom.scent_direction': { attr: 'scntDirection', list: true },
    'custom.presence': { attr: 'scntPresence', list: false }
  };

  var CLOSE_SVG = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"/></svg>';

  function slice(nodes) { return Array.prototype.slice.call(nodes); }

  function splitList(v) {
    if (!v) return [];
    return v.split('||').map(function (s) { return s.trim(); }).filter(Boolean);
  }

  function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function Drawer(root) {
    this.root = root;
    this.panel = root.querySelector('.scnt-fdrawer__panel');
    this.backdrop = root.querySelector('[data-scnt-fdrawer-backdrop]');
    this.closeBtn = root.querySelector('[data-scnt-fdrawer-close]');
    this.clearBtn = root.querySelector('[data-scnt-fdrawer-clear]');
    this.clearAllBtn = root.querySelector('[data-scnt-fdrawer-clearall]');
    this.applyBtn = root.querySelector('[data-scnt-fdrawer-apply]');
    this.applyLabel = root.querySelector('[data-scnt-apply-label]');
    this.activeWrap = root.querySelector('[data-scnt-active]');
    this.pillsWrap = root.querySelector('[data-scnt-active-pills]');
    this.chips = slice(root.querySelectorAll('[data-scnt-fchip]'));
    this.sliders = slice(root.querySelectorAll('[data-scnt-fslider]'));
    this.countSpans = slice(root.querySelectorAll('[data-scnt-count]'));
    this.baseUrl = root.getAttribute('data-base-url') || window.location.pathname;
    this.selected = new Map(); // key -> Set(values)
    this.lastFocused = null;
    this.countEl = document.querySelector('.scnt-shop-toolbar__count');
    this.countOriginal = this.countEl ? this.countEl.innerHTML : null;
    this.hydrateFromUrl();
    this.bind();
    this.syncUI();
    if (this.selected.size) this.applyToGrid();
  }

  /* ---------- grid cards ---------- */
  Drawer.prototype.cards = function () {
    return slice(document.querySelectorAll('[data-scnt-card]'));
  };

  Drawer.prototype.cardValues = function (card, key) {
    var meta = ATTR[key];
    if (!meta) return [];
    var raw = card.dataset[meta.attr] || '';
    return meta.list ? splitList(raw) : (raw ? [raw.trim()] : []);
  };

  Drawer.prototype.cardMatches = function (card) {
    var self = this;
    var ok = true;
    this.selected.forEach(function (set, key) {
      if (!ok) return;
      var vals = self.cardValues(card, key);
      var hit = false;
      set.forEach(function (v) { if (vals.indexOf(v) > -1) hit = true; });
      if (!hit) ok = false;
    });
    return ok;
  };

  Drawer.prototype.matchCount = function () {
    var self = this;
    var cards = this.cards();
    if (!cards.length) return null;
    if (!this.selected.size) return cards.length;
    var n = 0;
    cards.forEach(function (c) { if (self.cardMatches(c)) n++; });
    return n;
  };

  /* ---------- reflect state into the drawer controls ---------- */
  Drawer.prototype.syncUI = function () {
    var self = this;

    this.chips.forEach(function (chip) {
      var key = chip.getAttribute('data-key');
      var value = chip.getAttribute('data-value');
      var set = self.selected.get(key);
      chip.setAttribute('aria-pressed', set && set.has(value) ? 'true' : 'false');
    });

    this.sliders.forEach(function (sl) {
      var key = sl.getAttribute('data-key');
      var values = self.sliderValues(sl);
      var set = self.selected.get(key);
      var idx = 0;
      if (set) {
        for (var i = 0; i < values.length; i++) {
          if (set.has(values[i])) { idx = i + 1; break; }
        }
      }
      sl.value = String(idx);
      var ro = self.root.querySelector('[data-scnt-readout="' + key + '"]');
      if (ro) ro.textContent = idx > 0 ? values[idx - 1] : 'Any';
    });

    this.countSpans.forEach(function (span) {
      var key = span.getAttribute('data-scnt-count');
      var set = self.selected.get(key);
      span.textContent = set && set.size ? '· ' + set.size : '';
    });

    this.renderPills();
    this.updateApply();
  };

  Drawer.prototype.sliderValues = function (sl) {
    return (sl.getAttribute('data-values') || '').split(',').map(function (s) { return s.trim(); });
  };

  /* ---------- active pills ---------- */
  Drawer.prototype.renderPills = function () {
    var self = this;
    if (!this.pillsWrap) return;
    this.pillsWrap.innerHTML = '';
    var any = false;
    this.selected.forEach(function (set, key) {
      set.forEach(function (value) {
        any = true;
        var pill = document.createElement('button');
        pill.type = 'button';
        pill.className = 'scnt-fdrawer__pill';
        pill.setAttribute('aria-label', 'Remove ' + value);
        pill.innerHTML = '<span>' + escapeHtml(value) + '</span>' + CLOSE_SVG;
        pill.addEventListener('click', function () { self.removeValue(key, value); });
        self.pillsWrap.appendChild(pill);
      });
    });
    if (this.activeWrap) this.activeWrap.hidden = !any;
  };

  Drawer.prototype.removeValue = function (key, value) {
    var set = this.selected.get(key);
    if (set) {
      set.delete(value);
      if (!set.size) this.selected.delete(key);
    }
    this.syncUI();
  };

  /* ---------- interactions ---------- */
  Drawer.prototype.toggleChip = function (chip) {
    var key = chip.getAttribute('data-key');
    var value = chip.getAttribute('data-value');
    if (!key || !value) return;
    var set = this.selected.get(key);
    if (!set) { set = new Set(); this.selected.set(key, set); }
    if (set.has(value)) {
      set.delete(value);
      if (!set.size) this.selected.delete(key);
    } else {
      set.add(value);
    }
    this.syncUI();
  };

  Drawer.prototype.onSlider = function (sl) {
    var key = sl.getAttribute('data-key');
    var values = this.sliderValues(sl);
    var idx = parseInt(sl.value, 10) || 0;
    if (idx <= 0) {
      this.selected.delete(key);
    } else {
      var set = new Set();
      set.add(values[idx - 1]);
      this.selected.set(key, set);
    }
    this.syncUI();
  };

  Drawer.prototype.updateApply = function () {
    if (!this.applyLabel) return;
    var c = this.matchCount();
    if (c === null) {
      this.applyLabel.textContent = 'Apply';
    } else if (c === 0) {
      this.applyLabel.textContent = 'No matches';
    } else {
      this.applyLabel.textContent = 'Show ' + c + ' result' + (c === 1 ? '' : 's');
    }
  };

  /* ---------- apply to the grid (client-side show/hide) ---------- */
  Drawer.prototype.applyToGrid = function () {
    var self = this;
    var cards = this.cards();
    if (!cards.length) { window.location.href = this.buildHref(); return; }
    var visible = 0;
    cards.forEach(function (card) {
      var show = !self.selected.size || self.cardMatches(card);
      card.hidden = !show;
      card.style.display = show ? '' : 'none';
      if (show) visible++;
    });
    this.updateGridCount(visible, cards.length);
    this.toggleEmpty(visible === 0);
    this.syncSortForm();
  };

  Drawer.prototype.updateGridCount = function (visible, total) {
    if (!this.countEl) return;
    if (this.selected.size) {
      this.countEl.textContent = visible + (visible === 1 ? ' product' : ' products');
    } else if (this.countOriginal !== null) {
      this.countEl.innerHTML = this.countOriginal;
    }
  };

  Drawer.prototype.toggleEmpty = function (isEmpty) {
    var grid = document.getElementById('product-grid');
    if (!grid) return;
    var msg = document.getElementById('ScntNoResults');
    if (!msg) {
      msg = document.createElement('div');
      msg.id = 'ScntNoResults';
      msg.className = 'scnt-fdrawer-empty';
      msg.innerHTML = '<p>No fragrances match these filters.</p>' +
        '<button type="button" class="scnt-fdrawer__btn scnt-fdrawer__clear" data-scnt-empty-clear>Clear filters</button>';
      grid.parentNode.insertBefore(msg, grid.nextSibling);
      var self = this;
      msg.querySelector('[data-scnt-empty-clear]').addEventListener('click', function () { self.clear(); });
    }
    msg.style.display = isEmpty ? '' : 'none';
  };

  /* keep the sort form carrying the active filters so sorting preserves them */
  Drawer.prototype.syncSortForm = function () {
    var form = document.querySelector('.scnt-shop-toolbar__sort');
    if (!form) return;
    slice(form.querySelectorAll('[data-scnt-sort-filter]')).forEach(function (n) { n.parentNode.removeChild(n); });
    this.selected.forEach(function (set, key) {
      set.forEach(function (value) {
        var input = document.createElement('input');
        input.type = 'hidden';
        input.name = 'filter.p.m.' + key;
        input.value = value;
        input.setAttribute('data-scnt-sort-filter', '');
        form.appendChild(input);
      });
    });
  };

  /* ---------- href / url ---------- */
  Drawer.prototype.buildHref = function () {
    var params = [];
    this.selected.forEach(function (set, key) {
      set.forEach(function (value) {
        params.push('filter.p.m.' + key + '=' + encodeURIComponent(value));
      });
    });
    return params.length ? this.baseUrl + '?' + params.join('&') : this.baseUrl;
  };

  Drawer.prototype.pushUrl = function () {
    if (!window.history || !window.history.replaceState) return;
    try { window.history.replaceState({}, '', this.buildHref()); } catch (e) { /* no-op */ }
  };

  Drawer.prototype.hydrateFromUrl = function () {
    var self = this;
    var params = new URLSearchParams(window.location.search);
    params.forEach(function (value, name) {
      if (name.indexOf('filter.p.m.') !== 0) return;
      var key = name.slice('filter.p.m.'.length);
      if (!ATTR[key]) return;
      if (!self.selected.has(key)) self.selected.set(key, new Set());
      self.selected.get(key).add(value);
    });
  };

  Drawer.prototype.clear = function () {
    this.selected.clear();
    this.syncUI();
    this.applyToGrid();
    this.pushUrl();
  };

  Drawer.prototype.apply = function () {
    this.applyToGrid();
    this.pushUrl();
    this.close();
  };

  /* ---------- open / close ---------- */
  Drawer.prototype.open = function () {
    if (!this.root.hidden) return;
    this.lastFocused = document.activeElement;
    this.root.hidden = false;
    document.body.classList.add('scnt-shop-locked');
    document.addEventListener('keydown', this.onKeydown);
  };

  Drawer.prototype.close = function () {
    if (this.root.hidden) return;
    this.root.hidden = true;
    document.body.classList.remove('scnt-shop-locked');
    document.removeEventListener('keydown', this.onKeydown);
    if (this.lastFocused && this.lastFocused.focus) {
      try { this.lastFocused.focus(); } catch (e) { /* no-op */ }
    }
  };

  Drawer.prototype.bind = function () {
    var self = this;
    this.onKeydown = function (e) {
      if (e.key === 'Escape' || e.key === 'Esc') self.close();
    };
    if (this.backdrop) this.backdrop.addEventListener('click', function () { self.close(); });
    if (this.closeBtn) this.closeBtn.addEventListener('click', function () { self.close(); });
    if (this.clearBtn) this.clearBtn.addEventListener('click', function () { self.clear(); });
    if (this.clearAllBtn) this.clearAllBtn.addEventListener('click', function () { self.clear(); });
    if (this.applyBtn) {
      this.applyBtn.addEventListener('click', function (e) {
        e.preventDefault();
        self.apply();
      });
    }
    this.chips.forEach(function (chip) {
      chip.addEventListener('click', function () { self.toggleChip(chip); });
    });
    this.sliders.forEach(function (sl) {
      sl.addEventListener('input', function () { self.onSlider(sl); });
      sl.addEventListener('change', function () { self.onSlider(sl); });
    });
  };

  function getInstance() {
    var el = document.querySelector('[data-scnt-fdrawer]');
    if (!el) return null;
    if (el.parentNode !== document.body) document.body.appendChild(el);
    if (!el.scntDrawer) el.scntDrawer = new Drawer(el);
    return el.scntDrawer;
  }

  function init() {
    var inst = getInstance();
    if (!inst) return;
    if (window.__scntFdrawerDelegated) return;
    window.__scntFdrawerDelegated = true;
    document.addEventListener('click', function (e) {
      var trigger = e.target.closest('[data-scnt-filter-open]');
      if (!trigger) return;
      e.preventDefault();
      var instance = getInstance();
      if (instance) instance.open();
    });
  }

  if (document.readyState !== 'loading') init();
  else document.addEventListener('DOMContentLoaded', init);

  document.addEventListener('shopify:section:load', init);
})();
